package org.gymly.ui.admin

import androidx.compose.foundation.clickable
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.ContentCopy
import androidx.compose.material.icons.outlined.Close
import androidx.compose.material.icons.outlined.Delete
import androidx.compose.material.icons.outlined.Edit
import androidx.compose.material.icons.outlined.EventBusy
import androidx.compose.material.icons.outlined.Search
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Checkbox
import androidx.compose.material3.CheckboxDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.staticCompositionLocalOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import kotlinx.coroutines.launch
import org.gymly.data.AdminRepo
import org.gymly.model.AdminCatalogService
import org.gymly.model.AdminSettingsPermissionModule
import org.gymly.model.AdminMembershipItem
import org.gymly.model.AdminScheduleKind
import org.gymly.model.AdminScheduleSlot
import org.gymly.model.AdminScheduleUpsert
import org.gymly.model.AdminServiceType
import org.gymly.model.AdminTrainer
import org.gymly.model.AdminWeekday
import org.gymly.model.AdminWorkDaySchedule
import org.gymly.theme.AdminColors
import org.gymly.theme.GymlySpace
import org.gymly.util.AdminDateOutputFormat
import org.gymly.util.WeekRange
import org.gymly.util.mondayOfWeek
import org.gymly.util.parseFlexibleDateToIso
import org.gymly.util.userMessage
import org.gymly.ui.components.AdminDateField
import org.gymly.ui.components.VerticalScrollColumn

private enum class SchedulesDateFilter(val label: String) {
    Week("Por semana"),
    All("Todos"),
}

private sealed class SchedulesDialog {
    data object Create : SchedulesDialog()
    data object DuplicateWeek : SchedulesDialog()
    data class Edit(val item: AdminScheduleSlot) : SchedulesDialog()
    data class Detail(val item: AdminScheduleSlot) : SchedulesDialog()
    data class Delete(val item: AdminScheduleSlot) : SchedulesDialog()
    data class MarkAbsent(val item: AdminScheduleSlot) : SchedulesDialog()
}

/** Orden alfabético por nombre del horario, luego entrenador. */
private fun AdminScheduleSlot.nameSortKey(): String =
    title.trim().lowercase() + "\u0000" + (trainerName ?: "").trim().lowercase()

private val ScheduleAbsentColor = Color(0xFFC62828)

private fun parseTimeRange(raw: String): Pair<String, String>? {
    val cleaned = raw.trim()
        .replace('–', '-')
        .replace('—', '-')
        .replace(" a ", "-", ignoreCase = true)
    val parts = cleaned.split('-').map { it.trim() }.filter { it.isNotEmpty() }
    if (parts.size != 2) return null
    val start = normalizeHhMm(parts[0]) ?: return null
    val end = normalizeHhMm(parts[1]) ?: return null
    return start to end
}

/** Qué acciones muestra la pantalla según los permisos de Horarios. El servidor aplica la regla final. */
private class ScheduleAccess(val canWrite: Boolean, val canDelete: Boolean)

private val LocalScheduleAccess = staticCompositionLocalOf { ScheduleAccess(canWrite = false, canDelete = false) }

@Composable
fun AdminSchedulesScreen(permissions: Set<AdminSettingsPermissionModule> = emptySet()) {
    val access = ScheduleAccess(
        // Usar o Aprobar: puede crear y editar (con Usar, la acción pide aprobación).
        canWrite = permissions.any {
            it == AdminSettingsPermissionModule.SCHEDULE_OPERATE || it == AdminSettingsPermissionModule.SCHEDULE_APPROVE
        },
        // Borrar, Usar o Aprobar: puede pedir o ejecutar el borrado.
        canDelete = permissions.any {
                it == AdminSettingsPermissionModule.SCHEDULE_DELETE ||
                it == AdminSettingsPermissionModule.SCHEDULE_APPROVE
        },
    )
    CompositionLocalProvider(LocalScheduleAccess provides access) {
        AdminSchedulesContent()
    }
}

@Composable
private fun AdminSchedulesContent() {
    val pendingApprovals = LocalPendingApprovalNotifier.current
    val scope = rememberCoroutineScope()
    var items by remember { mutableStateOf<List<AdminScheduleSlot>>(emptyList()) }
    var trainers by remember { mutableStateOf<List<AdminTrainer>>(emptyList()) }
    var services by remember { mutableStateOf<List<AdminCatalogService>>(emptyList()) }
    var companyHours by remember { mutableStateOf<List<AdminWorkDaySchedule>>(emptyList()) }
    var memberships by remember { mutableStateOf<List<AdminMembershipItem>>(emptyList()) }
    var schedulesAutomatic by remember { mutableStateOf(false) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var query by remember { mutableStateOf("") }
    var filterServiceId by remember { mutableStateOf<String?>(null) }
    var filterTrainerId by remember { mutableStateOf<String?>(null) }
    var dateFilter by remember { mutableStateOf(SchedulesDateFilter.Week) }
    var weekOffset by remember { mutableStateOf(0) }
    var dialog by remember { mutableStateOf<SchedulesDialog?>(null) }
    var saving by remember { mutableStateOf(false) }
    var successMsg by remember { mutableStateOf<String?>(null) }

    val week = remember(weekOffset) { WeekRange.ofOffset(weekOffset) }

    fun reload() {
        scope.launch {
            loading = true
            error = null
            try {
                val weekStart = if (dateFilter == SchedulesDateFilter.Week) week.mondayIso else null
                items = AdminRepo.api.listSchedules(weekStart)
                services = AdminRepo.api.listServices().filter { it.active }
                companyHours = runCatching { AdminRepo.api.getCompany().operatingHours }.getOrDefault(emptyList())
                memberships = runCatching { AdminRepo.api.listMemberships() }.getOrDefault(emptyList())
                schedulesAutomatic = runCatching {
                    AdminRepo.api.getSettingsModules().schedulesAutomatic
                }.getOrDefault(false)
                val fromApi = runCatching { AdminRepo.api.listTrainers() }.getOrDefault(emptyList())
                trainers = fromApi.ifEmpty {
                    AdminRepo.api.listWorkers()
                        .filter {
                            it.role == org.gymly.model.AdminStaffRole.TRAINER ||
                                it.role == org.gymly.model.AdminStaffRole.ADMIN
                        }
                        .map { AdminTrainer(it.id, it.displayName, it.email, it.role.name) }
                }
            } catch (e: Exception) {
                error = e.userMessage("No se pudieron cargar los horarios.")
            } finally {
                loading = false
            }
        }
    }

    LaunchedEffect(weekOffset, dateFilter) { reload() }

    val filtered = remember(items, query, filterServiceId, filterTrainerId) {
        val q = query.trim().lowercase()
        items.filter { s ->
            val haystack = listOfNotNull(
                s.slotLabel(),
                s.title,
                s.trainerName,
                s.serviceName,
                s.capacity.toString(),
            ).joinToString(" ").lowercase()
            val matchesQuery = q.isEmpty() || haystack.contains(q)
            val matchesService = filterServiceId == null || s.serviceId == filterServiceId
            val matchesTrainer = filterTrainerId == null || s.trainerId == filterTrainerId
            matchesQuery && matchesService && matchesTrainer
        }.sortedWith(
            compareBy<AdminScheduleSlot> { it.nameSortKey() }
                .thenBy { it.weekday }
                .thenBy { it.startTime },
        )
    }

    val enrolledByTrainerDay = remember(memberships, filtered, week) {
        val days = AdminWeekday.WORK_WEEK.map { week.dateForWeekday(it.iso).take(10) }
        val trainerIds = filtered.mapNotNull { it.trainerId }
        enrolledCountsByTrainerDay(memberships, trainerIds, days)
    }

    val total = filtered.size
    val totalCapacity = filtered.sumOf { it.capacity }
    val daysCovered = filtered.map { it.weekday }.toSet().size
    val trainerCount = filtered.mapNotNull { it.trainerId }.toSet().size

    BoxWithConstraints(
        Modifier
            .fillMaxSize()
            .background(AdminColors.Surface)
            .padding(GymlySpace.lg),
    ) {
        val compact = maxWidth < 700.dp
        // Celular: la página se desplaza y la cuadrícula tiene altura fija, así siempre se ve.
        Column(Modifier.fillMaxSize().then(if (compact) Modifier.verticalScroll(rememberScrollState()) else Modifier)) {
            SchedulesHeader(
                dateFilter = dateFilter,
                week = week,
                onFilterChange = { dateFilter = it },
                onWeekOffsetChange = { weekOffset = it },
                compact = compact,
            )
            Spacer(Modifier.height(16.dp))
            SchedulesKpiRow(total, totalCapacity, daysCovered, trainerCount, compact)
            Spacer(Modifier.height(16.dp))
            error?.let {
                Text(it, color = AdminColors.Error, fontSize = 13.sp, modifier = Modifier.padding(bottom = 8.dp))
            }
            successMsg?.let {
                Text(it, color = AdminColors.Primary, fontSize = 13.sp, modifier = Modifier.padding(bottom = 8.dp))
            }
            SchedulesDirectoryCard(
                schedules = filtered,
                companyHours = companyHours,
                week = if (dateFilter == SchedulesDateFilter.Week) week else null,
                enrollmentWeek = week,
                enrolledByTrainerDay = enrolledByTrainerDay,
                loading = loading,
                query = query,
                onQueryChange = { query = it },
                catalogServices = services,
                trainers = trainers,
                filterServiceId = filterServiceId,
                onFilterServiceId = { filterServiceId = it },
                filterTrainerId = filterTrainerId,
                onFilterTrainerId = { filterTrainerId = it },
                compact = compact,
                showDuplicateWeek = !schedulesAutomatic && dateFilter == SchedulesDateFilter.Week,
                showCreate = !schedulesAutomatic,
                onCreate = { dialog = SchedulesDialog.Create },
                onDuplicateWeek = { dialog = SchedulesDialog.DuplicateWeek },
                onView = { dialog = SchedulesDialog.Detail(it) },
                onEdit = { dialog = SchedulesDialog.Edit(it) },
                onMarkAbsent = { dialog = SchedulesDialog.MarkAbsent(it) },
                onDelete = { dialog = SchedulesDialog.Delete(it) },
            )
        }
    }

    when (val d = dialog) {
        is SchedulesDialog.Detail -> ScheduleDetailDialog(d.item) { dialog = null }
        is SchedulesDialog.Create -> ScheduleFormDialog(
            title = "Agregar horario",
            initial = null,
            defaultWeekStart = if (dateFilter == SchedulesDateFilter.Week) week.mondayIso else null,
            trainers = trainers,
            services = services,
            saving = saving,
            onDismiss = { if (!saving) dialog = null },
            onSave = { upsert ->
                scope.launch {
                    saving = true
                    error = null
                    try {
                        AdminRepo.api.createSchedule(upsert)
                        dialog = null
                        reload()
                    } catch (e: Exception) {
                        if (pendingApprovals.offer(e)) dialog = null else error = e.userMessage("No se pudo crear el horario.")
                    } finally {
                        saving = false
                    }
                }
            },
        )
        is SchedulesDialog.DuplicateWeek -> DuplicateWeekDialog(
            sourceWeek = week,
            saving = saving,
            onDismiss = { if (!saving) dialog = null },
            onDuplicate = { targetDate, replaceExisting ->
                scope.launch {
                    saving = true
                    error = null
                    successMsg = null
                    try {
                        val copied = AdminRepo.api.duplicateScheduleWeek(
                            sourceWeekStart = week.mondayIso,
                            targetWeekStart = targetDate,
                            replaceExisting = replaceExisting,
                        )
                        val targetMonday = mondayOfWeek(targetDate)
                        weekOffset = WeekRange.offsetForMonday(targetMonday)
                        dialog = null
                        successMsg = "Semana duplicada: $copied horario(s) copiados."
                    } catch (e: Exception) {
                        error = e.userMessage("No se pudo duplicar la semana.")
                    } finally {
                        saving = false
                    }
                }
            },
        )
        is SchedulesDialog.Edit -> ScheduleFormDialog(
            title = "Editar horario",
            initial = d.item,
            defaultWeekStart = d.item.weekStart,
            trainers = trainers,
            services = services,
            saving = saving,
            onDismiss = { if (!saving) dialog = null },
            onSave = { upsert ->
                scope.launch {
                    saving = true
                    error = null
                    try {
                        AdminRepo.api.updateSchedule(d.item.id, upsert)
                        dialog = null
                        reload()
                    } catch (e: Exception) {
                        if (pendingApprovals.offer(e)) dialog = null else error = e.userMessage("No se pudo guardar el horario.")
                    } finally {
                        saving = false
                    }
                }
            },
        )
        is SchedulesDialog.MarkAbsent -> AlertDialog(
            onDismissRequest = { if (!saving) dialog = null },
            title = {
                Text(
                    if (d.item.trainerAbsent) "Quitar inasistencia" else "Inasistencia",
                )
            },
            text = {
                val who = d.item.trainerName ?: "el entrenador"
                Text(
                    if (d.item.trainerAbsent) {
                        "¿Quitar la inasistencia de $who en ${d.item.slotLabel()}?"
                    } else {
                        "¿Registrar inasistencia de $who en ${d.item.slotLabel()}? El horario quedará marcado en rojo."
                    },
                )
            },
            confirmButton = {
                Button(
                    onClick = {
                        scope.launch {
                            saving = true
                            error = null
                            try {
                                AdminRepo.api.setScheduleTrainerAbsent(
                                    d.item.id,
                                    absent = !d.item.trainerAbsent,
                                )
                                dialog = null
                                successMsg = if (d.item.trainerAbsent) {
                                    "Inasistencia quitada."
                                } else {
                                    "Inasistencia registrada."
                                }
                                reload()
                            } catch (e: Exception) {
                                error = e.userMessage("No se pudo actualizar la inasistencia.")
                            } finally {
                                saving = false
                            }
                        }
                    },
                    enabled = !saving,
                    colors = ButtonDefaults.buttonColors(
                        containerColor = if (d.item.trainerAbsent) AdminColors.Primary else ScheduleAbsentColor,
                        contentColor = AdminColors.OnBrand,
                    ),
                ) {
                    Text(
                        when {
                            saving -> "…"
                            d.item.trainerAbsent -> "Quitar inasistencia"
                            else -> "Inasistencia"
                        },
                    )
                }
            },
            dismissButton = {
                TextButton(onClick = { dialog = null }, enabled = !saving) {
                    Text("Cancelar", color = AdminColors.TextMuted)
                }
            },
        )
        is SchedulesDialog.Delete -> AlertDialog(
            onDismissRequest = { if (!saving) dialog = null },
            title = { Text("Eliminar horario") },
            text = {
                Text("¿Seguro que deseas eliminar el horario ${d.item.slotLabel()}? Esta acción no se puede deshacer.")
            },
            confirmButton = {
                Button(
                    onClick = {
                        scope.launch {
                            saving = true
                            error = null
                            try {
                                AdminRepo.api.deleteSchedule(d.item.id)
                                dialog = null
                                reload()
                            } catch (e: Exception) {
                                if (pendingApprovals.offer(e)) dialog = null else error = e.userMessage("No se pudo eliminar el horario.")
                            } finally {
                                saving = false
                            }
                        }
                    },
                    enabled = !saving,
                    colors = ButtonDefaults.buttonColors(
                        containerColor = AdminColors.Primary,
                        contentColor = AdminColors.OnBrand,
                    ),
                ) {
                    Text(if (saving) "…" else "Eliminar")
                }
            },
            dismissButton = {
                TextButton(onClick = { dialog = null }, enabled = !saving) {
                    Text("Cancelar", color = AdminColors.TextMuted)
                }
            },
        )
        null -> Unit
    }
}

@Composable
private fun SchedulesHeader(
    dateFilter: SchedulesDateFilter,
    week: WeekRange,
    onFilterChange: (SchedulesDateFilter) -> Unit,
    onWeekOffsetChange: (Int) -> Unit,
    compact: Boolean = false,
) {
    var menuOpen by remember { mutableStateOf(false) }
    val controls: @Composable () -> Unit = {
        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
            if (dateFilter == SchedulesDateFilter.Week) {
                WeekNavigator(week = week, onWeekOffsetChange = onWeekOffsetChange)
            }
            Box {
                OutlinedButton(onClick = { menuOpen = true }, shape = RoundedCornerShape(10.dp)) {
                    Text(dateFilter.label, color = AdminColors.Text)
                }
                DropdownMenu(expanded = menuOpen, onDismissRequest = { menuOpen = false }) {
                    SchedulesDateFilter.entries.forEach { opt ->
                        DropdownMenuItem(
                            text = { Text(opt.label) },
                            onClick = {
                                onFilterChange(opt)
                                menuOpen = false
                            },
                        )
                    }
                }
            }
        }
    }
    if (compact) {
        // Celular: título a todo el ancho y los controles debajo, para que el título no se aplaste.
        Column(Modifier.fillMaxWidth()) {
            Text("Horarios Semanales", fontSize = 24.sp, fontWeight = FontWeight.Bold, color = AdminColors.Text)
            Text(
                "Franjas reservables recurrentes desde /api/schedules (entrenador, día, hora, servicio, capacidad).",
                color = AdminColors.TextMuted,
                fontSize = 13.sp,
                modifier = Modifier.padding(top = 4.dp),
            )
            Spacer(Modifier.height(10.dp))
            controls()
        }
        return
    }
    Row(
        Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.Top,
    ) {
        Column(Modifier.weight(1f)) {
            Text("Horarios Semanales", fontSize = 24.sp, fontWeight = FontWeight.Bold, color = AdminColors.Text)
            Text(
                "Franjas reservables recurrentes desde /api/schedules (entrenador, día, hora, servicio, capacidad).",
                color = AdminColors.TextMuted,
                fontSize = 13.sp,
                modifier = Modifier.padding(top = 4.dp),
            )
        }
        controls()
    }
}

@Composable
private fun SchedulesKpiRow(
    total: Int,
    totalCapacity: Int,
    daysCovered: Int,
    trainers: Int,
    compact: Boolean,
) {
    val kpiItems = listOf(
        Triple("Total de horarios", total.toString(), AdminColors.Primary),
        Triple("Capacidad total", totalCapacity.toString(), Color(0xFF2E7D32)),
        Triple("Días cubiertos", daysCovered.toString(), Color(0xFFF59E0B)),
        Triple("Entrenadores", trainers.toString(), Color(0xFF1565C0)),
    )
    if (compact) {
        Column(Modifier.fillMaxWidth().horizontalScroll(rememberScrollState())) {
            Row(horizontalArrangement = Arrangement.spacedBy(10.dp)) {
                kpiItems.forEach { (label, value, accent) ->
                    SchedulesKpiCard(label, value, accent, Modifier.width(150.dp))
                }
            }
        }
    } else {
        Row(horizontalArrangement = Arrangement.spacedBy(12.dp), modifier = Modifier.fillMaxWidth()) {
            kpiItems.forEach { (label, value, accent) ->
                SchedulesKpiCard(label, value, accent, Modifier.weight(1f))
            }
        }
    }
}

@Composable
private fun SchedulesKpiCard(label: String, value: String, accent: Color, modifier: Modifier = Modifier) {
    Column(
        modifier
            .clip(RoundedCornerShape(14.dp))
            .background(AdminColors.SurfaceContainer)
            .padding(16.dp),
    ) {
        Text(label, color = AdminColors.TextMuted, fontSize = 12.sp)
        Spacer(Modifier.height(6.dp))
        Text(value, fontWeight = FontWeight.Bold, fontSize = 22.sp, color = accent)
    }
}

@Composable
private fun ColumnScope.SchedulesDirectoryCard(
    schedules: List<AdminScheduleSlot>,
    companyHours: List<AdminWorkDaySchedule>,
    week: WeekRange?,
    enrollmentWeek: WeekRange,
    enrolledByTrainerDay: Map<String, Int>,
    loading: Boolean,
    query: String,
    onQueryChange: (String) -> Unit,
    catalogServices: List<AdminCatalogService>,
    trainers: List<AdminTrainer>,
    filterServiceId: String?,
    onFilterServiceId: (String?) -> Unit,
    filterTrainerId: String?,
    onFilterTrainerId: (String?) -> Unit,
    compact: Boolean,
    showDuplicateWeek: Boolean,
    showCreate: Boolean = true,
    onCreate: () -> Unit,
    onDuplicateWeek: () -> Unit,
    onView: (AdminScheduleSlot) -> Unit,
    onEdit: (AdminScheduleSlot) -> Unit,
    onMarkAbsent: (AdminScheduleSlot) -> Unit,
    onDelete: (AdminScheduleSlot) -> Unit,
) {
    Column(
        Modifier
            .then(if (compact) Modifier.height(600.dp) else Modifier.weight(1f))
            .fillMaxWidth()
            .clip(RoundedCornerShape(16.dp))
            .background(AdminColors.SurfaceContainer)
            .padding(16.dp),
    ) {
        if (compact) {
            Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
                Text(
                    if (week != null) "Horarios · ${week.labelEs()}" else "Horarios semanales",
                    fontWeight = FontWeight.Bold,
                    fontSize = 16.sp,
                )
                SchedulesSearchField(query, onQueryChange)
                SchedulesFilterRow(
                    catalogServices = catalogServices,
                    trainers = trainers,
                    filterServiceId = filterServiceId,
                    onFilterServiceId = onFilterServiceId,
                    filterTrainerId = filterTrainerId,
                    onFilterTrainerId = onFilterTrainerId,
                )
                SchedulesStatusLegend()
                if (showDuplicateWeek || showCreate) {
                    Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                        if (showDuplicateWeek && LocalScheduleAccess.current.canWrite) {
                            OutlinedButton(
                                onClick = onDuplicateWeek,
                                modifier = Modifier.weight(1f).height(44.dp),
                                shape = RoundedCornerShape(10.dp),
                            ) {
                                Icon(Icons.Outlined.ContentCopy, contentDescription = null, tint = AdminColors.Primary)
                                Spacer(Modifier.width(6.dp))
                                Text("Duplicar semana", color = AdminColors.Primary, fontSize = 13.sp)
                            }
                        }
                        if (showCreate && LocalScheduleAccess.current.canWrite) {
                            Button(
                                onClick = onCreate,
                                modifier = Modifier.weight(1f).height(44.dp),
                                shape = RoundedCornerShape(10.dp),
                                colors = ButtonDefaults.buttonColors(
                                    containerColor = AdminColors.Primary,
                                    contentColor = AdminColors.OnBrand,
                                ),
                            ) {
                                Text("+ Nuevo horario", fontWeight = FontWeight.SemiBold)
                            }
                        }
                    }
                }
            }
        } else {
            Row(
                Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Text(
                    if (week != null) "Horarios · ${week.labelEs()}" else "Horarios semanales",
                    fontWeight = FontWeight.Bold,
                    fontSize = 16.sp,
                )
                Row(horizontalArrangement = Arrangement.spacedBy(10.dp), verticalAlignment = Alignment.CenterVertically) {
                    Box(Modifier.widthIn(max = 220.dp)) { SchedulesSearchField(query, onQueryChange) }
                    if (showDuplicateWeek && LocalScheduleAccess.current.canWrite) {
                        OutlinedButton(onClick = onDuplicateWeek, shape = RoundedCornerShape(10.dp)) {
                            Icon(Icons.Outlined.ContentCopy, contentDescription = null, tint = AdminColors.Primary)
                            Spacer(Modifier.width(6.dp))
                            Text("Duplicar semana", color = AdminColors.Primary)
                        }
                    }
                    if (showCreate && LocalScheduleAccess.current.canWrite) {
                        Button(
                            onClick = onCreate,
                            shape = RoundedCornerShape(10.dp),
                            colors = ButtonDefaults.buttonColors(
                                containerColor = AdminColors.Primary,
                                contentColor = AdminColors.OnBrand,
                            ),
                        ) {
                            Text("+ Nuevo horario", fontWeight = FontWeight.SemiBold)
                        }
                    }
                }
            }
            Spacer(Modifier.height(10.dp))
            Row(
                Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                SchedulesFilterRow(
                    catalogServices = catalogServices,
                    trainers = trainers,
                    filterServiceId = filterServiceId,
                    onFilterServiceId = onFilterServiceId,
                    filterTrainerId = filterTrainerId,
                    onFilterTrainerId = onFilterTrainerId,
                    modifier = Modifier.weight(1f),
                )
                Spacer(Modifier.width(12.dp))
                SchedulesStatusLegend()
            }
        }
        Spacer(Modifier.height(14.dp))
        Column(Modifier.weight(1f).fillMaxWidth()) {
            when {
                loading -> Box(Modifier.weight(1f).fillMaxWidth(), contentAlignment = Alignment.Center) {
                    CircularProgressIndicator(color = AdminColors.Primary)
                }
                schedules.isEmpty() && companyHours.isEmpty() -> Box(Modifier.weight(1f).fillMaxWidth()) {
                    Text(
                        "No hay horarios. Configura el horario de Empresa o crea franjas manualmente.",
                        color = AdminColors.TextMuted,
                    )
                }
                else -> SchedulesWeekGrid(
                    schedules = schedules,
                    companyHours = companyHours,
                    week = week,
                    enrollmentWeek = enrollmentWeek,
                    enrolledByTrainerDay = enrolledByTrainerDay,
                    onView = onView,
                    onEdit = onEdit,
                    onMarkAbsent = onMarkAbsent,
                    onDelete = onDelete,
                    compact = compact,
                    modifier = Modifier.weight(1f).fillMaxWidth(),
                )
            }
        }
    }
}

@Composable
private fun SchedulesFilterRow(
    catalogServices: List<AdminCatalogService>,
    trainers: List<AdminTrainer>,
    filterServiceId: String?,
    onFilterServiceId: (String?) -> Unit,
    filterTrainerId: String?,
    onFilterTrainerId: (String?) -> Unit,
    modifier: Modifier = Modifier,
) {
    var serviceMenu by remember { mutableStateOf(false) }
    var trainerMenu by remember { mutableStateOf(false) }
    val serviceLabel = catalogServices.firstOrNull { it.id == filterServiceId }?.name
    Row(
        modifier.fillMaxWidth().horizontalScroll(rememberScrollState()),
        horizontalArrangement = Arrangement.spacedBy(8.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Box {
            OutlinedButton(onClick = { serviceMenu = true }, shape = RoundedCornerShape(10.dp)) {
                Text(
                    serviceLabel?.let { "Servicio: $it" } ?: "Servicio: todos",
                    color = AdminColors.Text,
                    fontSize = 13.sp,
                )
            }
            DropdownMenu(expanded = serviceMenu, onDismissRequest = { serviceMenu = false }) {
                DropdownMenuItem(
                    text = { Text("Todos los servicios") },
                    onClick = {
                        onFilterServiceId(null)
                        serviceMenu = false
                    },
                )
                catalogServices.forEach { service ->
                    DropdownMenuItem(
                        text = { Text("${service.name} (${service.type.labelEs()})") },
                        onClick = {
                            onFilterServiceId(service.id)
                            serviceMenu = false
                        },
                    )
                }
            }
        }
        Box {
            val trainerLabel = trainers.firstOrNull { it.id == filterTrainerId }?.displayName
            OutlinedButton(onClick = { trainerMenu = true }, shape = RoundedCornerShape(10.dp)) {
                Text(
                    trainerLabel?.let { "Profesor: $it" } ?: "Profesor: todos",
                    color = AdminColors.Text,
                    fontSize = 13.sp,
                )
            }
            DropdownMenu(expanded = trainerMenu, onDismissRequest = { trainerMenu = false }) {
                DropdownMenuItem(
                    text = { Text("Todos los profesores") },
                    onClick = {
                        onFilterTrainerId(null)
                        trainerMenu = false
                    },
                )
                trainers.forEach { t ->
                    DropdownMenuItem(
                        text = { Text(t.displayName) },
                        onClick = {
                            onFilterTrainerId(t.id)
                            trainerMenu = false
                        },
                    )
                }
            }
        }
    }
}

@Composable
private fun SchedulesSearchField(query: String, onQueryChange: (String) -> Unit) {
    OutlinedTextField(
        value = query,
        onValueChange = onQueryChange,
        modifier = Modifier.fillMaxWidth(),
        singleLine = true,
        placeholder = { Text("Buscar...", color = AdminColors.TextMuted) },
        shape = RoundedCornerShape(10.dp),
        colors = OutlinedTextFieldDefaults.colors(
            focusedBorderColor = AdminColors.Primary,
            unfocusedBorderColor = AdminColors.Outline,
        ),
    )
}

@Composable
private fun SchedulesWeekGrid(
    schedules: List<AdminScheduleSlot>,
    companyHours: List<AdminWorkDaySchedule>,
    week: WeekRange?,
    enrollmentWeek: WeekRange,
    enrolledByTrainerDay: Map<String, Int>,
    onView: (AdminScheduleSlot) -> Unit,
    onEdit: (AdminScheduleSlot) -> Unit,
    onMarkAbsent: (AdminScheduleSlot) -> Unit,
    onDelete: (AdminScheduleSlot) -> Unit,
    compact: Boolean = false,
    modifier: Modifier = Modifier,
) {
    val weekdays = AdminWeekday.WORK_WEEK
    val timeRows = remember(companyHours, schedules) {
        hourlyRowsFromCompanyHours(companyHours).ifEmpty {
            hourlyRowsFromSchedules(schedules)
        }
    }
    val slotsByDay = remember(schedules) {
        schedules.groupBy { it.weekday }
    }
    val timeColumnWidth = if (compact) 52.dp else 64.dp
    val dayColumnWidth = if (compact) 108.dp else 0.dp // 0 = usar weight en pantallas anchas
    val fixedGridWidth = timeColumnWidth + dayColumnWidth * weekdays.size
    val horizontalScrollState = rememberScrollState()
    val verticalScrollState = rememberScrollState()
    // Día visible en celular (un día a la vez).
    var mobileDay by remember { mutableStateOf(weekdays.first()) }

    @Composable
    fun DayHeader(day: AdminWeekday, modifier: Modifier) {
        Column(
            modifier.padding(horizontal = 4.dp),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            Text(
                day.shortEs,
                fontWeight = FontWeight.Bold,
                fontSize = if (compact) 11.sp else 12.sp,
                color = AdminColors.Text,
                textAlign = TextAlign.Center,
                maxLines = 1,
            )
            if (week != null) {
                Text(
                    day.dateTag(week),
                    fontSize = 10.sp,
                    color = AdminColors.TextMuted,
                    textAlign = TextAlign.Center,
                    maxLines = 1,
                )
            }
        }
    }

    @Composable
    fun DayCell(day: AdminWeekday, timeKey: String, modifier: Modifier) {
        val cellSlots = slotsByDay[day.iso].orEmpty()
            .filter { slotOverlapsHourRow(it, timeKey) }
            .distinctBy { it.trainerId ?: it.id }
            .sortedBy { it.nameSortKey() }
        val dayIso = enrollmentWeek.dateForWeekday(day.iso).take(10)
        Box(modifier.padding(horizontal = 4.dp)) {
            if (cellSlots.isEmpty()) {
                Box(
                    Modifier
                        .fillMaxWidth()
                        .height(56.dp)
                        .clip(RoundedCornerShape(8.dp))
                        .background(AdminColors.Outline.copy(alpha = 0.12f)),
                )
            } else {
                ScheduleHourBlock(
                    slots = cellSlots,
                    dayIso = dayIso,
                    enrolledByTrainerDay = enrolledByTrainerDay,
                    onView = onView,
                    onEdit = onEdit,
                    onMarkAbsent = onMarkAbsent,
                    onDelete = onDelete,
                )
            }
        }
    }

    if (compact) {
        // Celular: un día a la vez. Botones de día arriba y la cuadrícula de ese día ocupa todo el ancho.
        val selectedDay = mobileDay
        Column(modifier.fillMaxWidth()) {
            Row(
                Modifier
                    .fillMaxWidth()
                    .horizontalScroll(horizontalScrollState),
                horizontalArrangement = Arrangement.spacedBy(6.dp),
            ) {
                weekdays.forEach { day ->
                    val on = day == selectedDay
                    Text(
                        day.shortEs,
                        fontWeight = FontWeight.SemiBold,
                        fontSize = 13.sp,
                        color = if (on) AdminColors.OnBrand else AdminColors.Text,
                        modifier = Modifier
                            .clip(RoundedCornerShape(16.dp))
                            .background(if (on) AdminColors.Primary else AdminColors.Surface)
                            .border(1.dp, AdminColors.Outline.copy(alpha = 0.55f), RoundedCornerShape(16.dp))
                            .clickable { mobileDay = day }
                            .padding(horizontal = 12.dp, vertical = 8.dp),
                    )
                }
            }
            Spacer(Modifier.height(10.dp))
            Row(
                Modifier
                    .fillMaxWidth()
                    .background(AdminColors.SurfaceContainer)
                    .padding(vertical = 8.dp),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Spacer(Modifier.width(timeColumnWidth))
                DayHeader(selectedDay, Modifier.weight(1f))
            }
            HorizontalDivider(color = AdminColors.Outline.copy(alpha = 0.5f))
            VerticalScrollColumn(
                Modifier
                    .weight(1f)
                    .fillMaxWidth(),
                state = verticalScrollState,
            ) {
                ScheduleWeekGridBody(
                    timeRows = timeRows,
                    weekdays = listOf(selectedDay),
                    timeColumnWidth = timeColumnWidth,
                    dayColumnWidth = dayColumnWidth,
                    gridWidth = null,
                    useWeight = true,
                    dayCell = { day, timeKey, mod -> DayCell(day, timeKey, mod) },
                )
            }
        }
    } else {
        Column(modifier.fillMaxWidth()) {
            Row(
                Modifier
                    .fillMaxWidth()
                    .background(AdminColors.SurfaceContainer)
                    .padding(vertical = 10.dp),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Spacer(Modifier.width(timeColumnWidth))
                weekdays.forEach { day ->
                    DayHeader(day, Modifier.weight(1f))
                }
            }
            HorizontalDivider(color = AdminColors.Outline.copy(alpha = 0.5f))
            VerticalScrollColumn(
                Modifier
                    .weight(1f)
                    .fillMaxWidth(),
                state = verticalScrollState,
            ) {
                ScheduleWeekGridBody(
                    timeRows = timeRows,
                    weekdays = weekdays,
                    timeColumnWidth = timeColumnWidth,
                    dayColumnWidth = dayColumnWidth,
                    gridWidth = null,
                    useWeight = true,
                    dayCell = { day, timeKey, mod -> DayCell(day, timeKey, mod) },
                )
            }
        }
    }
}

@Composable
private fun ScheduleWeekGridBody(
    timeRows: List<String>,
    weekdays: List<AdminWeekday>,
    timeColumnWidth: androidx.compose.ui.unit.Dp,
    dayColumnWidth: androidx.compose.ui.unit.Dp,
    gridWidth: androidx.compose.ui.unit.Dp?,
    useWeight: Boolean,
    dayCell: @Composable (AdminWeekday, String, Modifier) -> Unit,
) {
    if (timeRows.isEmpty()) {
        Text(
            "Sin franjas en esta semana.",
            color = AdminColors.TextMuted,
            modifier = Modifier.padding(16.dp),
        )
        return
    }
    timeRows.forEach { timeKey ->
        Row(
            Modifier
                .then(
                    when {
                        gridWidth != null -> Modifier.width(gridWidth)
                        else -> Modifier.fillMaxWidth()
                    },
                )
                .padding(vertical = 6.dp),
            verticalAlignment = Alignment.Top,
        ) {
            Text(
                timeKey.replace('-', '\n'),
                Modifier
                    .width(timeColumnWidth)
                    .padding(top = 8.dp, end = 4.dp),
                fontSize = 12.sp,
                fontWeight = FontWeight.SemiBold,
                color = AdminColors.TextMuted,
                lineHeight = 13.sp,
                textAlign = TextAlign.Center,
            )
            weekdays.forEach { day ->
                val cellModifier = if (useWeight) {
                    Modifier.weight(1f)
                } else {
                    Modifier.width(dayColumnWidth)
                }
                dayCell(day, timeKey, cellModifier)
            }
        }
        HorizontalDivider(color = AdminColors.Outline.copy(alpha = 0.25f))
    }
}

@Composable
private fun SchedulesStatusLegend() {
    Row(
        horizontalArrangement = Arrangement.spacedBy(14.dp),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        SchedulesStatusLegendItem(
            color = AdminColors.Primary.copy(alpha = 0.55f),
            fill = AdminColors.Primary.copy(alpha = 0.12f),
            label = "Activo",
        )
        SchedulesStatusLegendItem(
            color = ScheduleAbsentColor,
            fill = ScheduleAbsentColor.copy(alpha = 0.22f),
            label = "Inasistencia",
        )
    }
}

@Composable
private fun SchedulesStatusLegendItem(color: Color, fill: Color, label: String) {
    Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(6.dp)) {
        Box(
            Modifier
                .size(width = 18.dp, height = 14.dp)
                .clip(RoundedCornerShape(4.dp))
                .border(1.dp, color, RoundedCornerShape(4.dp))
                .background(fill),
        )
        Text(label, color = AdminColors.TextMuted, fontSize = 12.sp)
    }
}

@Composable
private fun ScheduleHourBlock(
    slots: List<AdminScheduleSlot>,
    dayIso: String,
    enrolledByTrainerDay: Map<String, Int>,
    onView: (AdminScheduleSlot) -> Unit,
    onEdit: (AdminScheduleSlot) -> Unit,
    onMarkAbsent: (AdminScheduleSlot) -> Unit,
    onDelete: (AdminScheduleSlot) -> Unit,
) {
    val anyAbsent = slots.any { it.trainerAbsent }
    Column(
        Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(10.dp))
            .border(
                if (anyAbsent) 1.5.dp else 1.dp,
                if (anyAbsent) ScheduleAbsentColor else AdminColors.Primary.copy(alpha = 0.35f),
                RoundedCornerShape(10.dp),
            )
            .background(
                if (anyAbsent) ScheduleAbsentColor.copy(alpha = 0.10f)
                else AdminColors.Primary.copy(alpha = 0.08f),
            )
            .padding(horizontal = 8.dp, vertical = 6.dp),
        verticalArrangement = Arrangement.spacedBy(4.dp),
    ) {
        slots.forEach { slot ->
            val enrolled = enrolledCountKey(slot.trainerId, dayIso)
                ?.let { enrolledByTrainerDay[it] }
                ?: 0
            val displayName = scheduleSlotDisplayName(slot)
            val nameColor = if (slot.trainerAbsent) ScheduleAbsentColor else AdminColors.Text
            Row(
                Modifier
                    .fillMaxWidth()
                    .clip(RoundedCornerShape(8.dp))
                    .clickable { onView(slot) }
                    .padding(vertical = 2.dp),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Text(
                    displayName,
                    modifier = Modifier.weight(1f),
                    fontWeight = FontWeight.SemiBold,
                    fontSize = 12.sp,
                    color = nameColor,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )
                // Inscritos / capacidad máx. (sin repetir tipo de franja)
                Text(
                    if (slot.trainerId != null) "$enrolled/${slot.capacity}" else "${slot.capacity}",
                    fontSize = 12.sp,
                    fontWeight = FontWeight.Bold,
                    color = if (slot.trainerAbsent) ScheduleAbsentColor else AdminColors.Primary,
                    modifier = Modifier.padding(start = 6.dp, end = 2.dp),
                )
                if (LocalScheduleAccess.current.canWrite) IconButton(
                    onClick = { onEdit(slot) },
                    modifier = Modifier.size(24.dp),
                ) {
                    Icon(Icons.Outlined.Edit, "Editar", tint = AdminColors.TextMuted, modifier = Modifier.size(14.dp))
                }
                if (LocalScheduleAccess.current.canWrite) IconButton(
                    onClick = { onMarkAbsent(slot) },
                    modifier = Modifier.size(24.dp),
                ) {
                    Icon(
                        Icons.Outlined.EventBusy,
                        if (slot.trainerAbsent) "Quitar inasistencia" else "Inasistencia",
                        tint = if (slot.trainerAbsent) ScheduleAbsentColor else AdminColors.TextMuted,
                        modifier = Modifier.size(14.dp),
                    )
                }
                if (LocalScheduleAccess.current.canDelete) IconButton(
                    onClick = { onDelete(slot) },
                    modifier = Modifier.size(24.dp),
                ) {
                    Icon(Icons.Outlined.Delete, "Eliminar", tint = AdminColors.Error, modifier = Modifier.size(14.dp))
                }
            }
        }
    }
}

/** Un solo nombre visible: preferir entrenador; evita título duplicado. */
private fun scheduleSlotDisplayName(slot: AdminScheduleSlot): String {
    val trainer = slot.trainerName?.trim().orEmpty()
    val title = slot.title.trim()
    return when {
        trainer.isNotEmpty() -> trainer
        title.isNotEmpty() -> title
        else -> "Sin nombre"
    }
}

@Composable
private fun ScheduleMobileCard(
    item: AdminScheduleSlot,
    week: WeekRange?,
    onView: (AdminScheduleSlot) -> Unit,
    onEdit: (AdminScheduleSlot) -> Unit,
    onMarkAbsent: (AdminScheduleSlot) -> Unit,
    onDelete: (AdminScheduleSlot) -> Unit,
) {
    val dateHint = week?.dateForWeekday(item.weekday)?.take(10)
    Column(
        Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(12.dp))
            .border(
                if (item.trainerAbsent) 1.5.dp else 1.dp,
                if (item.trainerAbsent) ScheduleAbsentColor else AdminColors.Outline.copy(alpha = 0.6f),
                RoundedCornerShape(12.dp),
            )
            .background(if (item.trainerAbsent) ScheduleAbsentColor.copy(alpha = 0.12f) else Color.Transparent)
            .padding(12.dp),
    ) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            Box(
                Modifier
                    .size(8.dp)
                    .clip(CircleShape)
                    .background(if (item.trainerAbsent) ScheduleAbsentColor else AdminColors.Primary),
            )
            Spacer(Modifier.width(8.dp))
            Text(item.slotLabel(), fontWeight = FontWeight.SemiBold, modifier = Modifier.weight(1f))
        }
        Spacer(Modifier.height(4.dp))
        Text(
            listOfNotNull(dateHint, item.trainerName, item.title).joinToString(" — "),
            color = AdminColors.TextMuted,
            fontSize = 12.sp,
        )
        Text(
            if (item.trainerAbsent) "Capacidad: ${item.capacity} · Inasistencia" else "Capacidad: ${item.capacity}",
            color = if (item.trainerAbsent) ScheduleAbsentColor else AdminColors.TextMuted,
            fontSize = 12.sp,
            fontWeight = if (item.trainerAbsent) FontWeight.SemiBold else FontWeight.Normal,
        )
        Row(horizontalArrangement = Arrangement.End, modifier = Modifier.fillMaxWidth()) {
            IconButton(onClick = { onView(item) }) {
                Icon(Icons.Outlined.Search, "Ver", tint = AdminColors.TextMuted)
            }
            if (LocalScheduleAccess.current.canWrite) IconButton(onClick = { onEdit(item) }) {
                Icon(Icons.Outlined.Edit, "Editar", tint = AdminColors.TextMuted)
            }
            if (LocalScheduleAccess.current.canWrite) IconButton(onClick = { onMarkAbsent(item) }) {
                Icon(
                    Icons.Outlined.EventBusy,
                    if (item.trainerAbsent) "Quitar inasistencia" else "Inasistencia",
                    tint = if (item.trainerAbsent) ScheduleAbsentColor else AdminColors.TextMuted,
                )
            }
            if (LocalScheduleAccess.current.canDelete) IconButton(onClick = { onDelete(item) }) {
                Icon(Icons.Outlined.Delete, "Eliminar", tint = AdminColors.Error)
            }
        }
    }
}

@Composable
private fun ScheduleDetailDialog(item: AdminScheduleSlot, onDismiss: () -> Unit) {
    Dialog(onDismissRequest = onDismiss) {
        Column(
            Modifier
                .widthIn(max = 440.dp)
                .fillMaxWidth()
                .clip(RoundedCornerShape(16.dp))
                .background(AdminColors.SurfaceContainer)
                .padding(20.dp),
        ) {
            Row(
                Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Text("Detalle del horario", fontWeight = FontWeight.Bold, fontSize = 18.sp)
                IconButton(onClick = onDismiss) {
                    Icon(Icons.Outlined.Close, "Cerrar", tint = AdminColors.TextMuted)
                }
            }
            Spacer(Modifier.height(8.dp))
            ScheduleDetailRow("Entrenador", item.trainerName ?: "—")
            ScheduleDetailRow("Turno", item.shiftDisplay())
            ScheduleDetailRow("Día", item.weekdayLabel())
            ScheduleDetailRow("Rango horario", item.timeRangeLabel())
            ScheduleDetailRow("Franja", item.title)
            ScheduleDetailRow(
                "Servicio",
                item.serviceName?.let { name ->
                    val typeLabel = item.serviceType?.labelEs()
                    if (typeLabel != null) "$name ($typeLabel)" else name
                } ?: "—",
            )
            ScheduleDetailRow("Capacidad", item.capacity.toString())
            ScheduleDetailRow(
                "Asistencia entrenador",
                if (item.trainerAbsent) "Inasistencia" else "Presente",
            )
            Spacer(Modifier.height(12.dp))
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
                Button(
                    onClick = onDismiss,
                    colors = ButtonDefaults.buttonColors(
                        containerColor = AdminColors.Primary,
                        contentColor = AdminColors.OnBrand,
                    ),
                ) {
                    Text("Cerrar")
                }
            }
        }
    }
}

@Composable
private fun ScheduleDetailRow(label: String, value: String) {
    Column(Modifier.padding(bottom = 10.dp)) {
        Text(label, color = AdminColors.TextMuted, fontSize = 12.sp)
        Text(value, color = AdminColors.Text, fontSize = 14.sp, fontWeight = FontWeight.Medium)
    }
}

@Composable
private fun ScheduleFormDialog(
    title: String,
    initial: AdminScheduleSlot?,
    defaultWeekStart: String?,
    trainers: List<AdminTrainer>,
    services: List<AdminCatalogService>,
    saving: Boolean,
    onDismiss: () -> Unit,
    onSave: (AdminScheduleUpsert) -> Unit,
) {
    var trainerId by remember(initial) { mutableStateOf(initial?.trainerId.orEmpty()) }
    var trainerQuery by remember(initial) { mutableStateOf(initial?.trainerName.orEmpty()) }
    var serviceId by remember(initial) { mutableStateOf(initial?.serviceId.orEmpty()) }
    var weekday by remember(initial) {
        mutableStateOf(AdminWeekday.fromIso(initial?.weekday ?: AdminWeekday.MONDAY.iso))
    }
    var timeRange by remember(initial) {
        mutableStateOf(
            if (initial != null) initial.timeRangeLabel() else "05:00–06:00",
        )
    }
    var titleField by remember(initial) { mutableStateOf(initial?.title.orEmpty()) }
    var capacityText by remember(initial) {
        mutableStateOf((initial?.capacity ?: 12).toString())
    }
    var kind by remember(initial) {
        mutableStateOf(initial?.kind ?: AdminScheduleKind.BOOKABLE_CLASS)
    }
    var formError by remember { mutableStateOf<String?>(null) }
    var trainerMenu by remember { mutableStateOf(false) }
    var dayMenu by remember { mutableStateOf(false) }
    var kindMenu by remember { mutableStateOf(false) }
    var serviceMenu by remember { mutableStateOf(false) }

    val filteredServices = remember(services, kind) {
        val expectedType = if (kind == AdminScheduleKind.BOOKABLE_CLASS) {
            AdminServiceType.CLASS
        } else {
            AdminServiceType.SESSION
        }
        services.filter { it.type == expectedType }
    }
    val selectedService = remember(filteredServices, serviceId) {
        filteredServices.firstOrNull { it.id == serviceId }
    }

    val filteredTrainers = remember(trainers, trainerQuery) {
        val q = trainerQuery.trim().lowercase()
        if (q.isEmpty()) trainers.take(12)
        else trainers.filter {
            it.displayName.lowercase().contains(q) ||
                (it.email?.lowercase()?.contains(q) == true)
        }.take(12)
    }

    Dialog(onDismissRequest = { if (!saving) onDismiss() }) {
        VerticalScrollColumn(
            Modifier
                .widthIn(max = 480.dp)
                .fillMaxWidth()
                .clip(RoundedCornerShape(16.dp))
                .background(AdminColors.SurfaceContainer)
                .padding(20.dp),
        ) {
            Row(
                Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Text(title, fontWeight = FontWeight.Bold, fontSize = 18.sp)
                IconButton(onClick = onDismiss, enabled = !saving) {
                    Icon(Icons.Outlined.Close, "Cerrar", tint = AdminColors.TextMuted)
                }
            }
            Spacer(Modifier.height(12.dp))

            Text("Tipo de franja", color = AdminColors.TextMuted, fontSize = 12.sp)
            Spacer(Modifier.height(4.dp))
            Box {
                OutlinedButton(
                    onClick = { kindMenu = true },
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(10.dp),
                ) {
                    Text(kind.labelEs, color = AdminColors.Text, modifier = Modifier.fillMaxWidth())
                }
                DropdownMenu(expanded = kindMenu, onDismissRequest = { kindMenu = false }) {
                    AdminScheduleKind.entries.forEach { k ->
                        DropdownMenuItem(
                            text = { Text(k.labelEs) },
                            onClick = {
                                kind = k
                                kindMenu = false
                                if (serviceId.isNotEmpty()) {
                                    val stillValid = filteredServices.any { it.id == serviceId }
                                    if (!stillValid) serviceId = ""
                                }
                            },
                        )
                    }
                }
            }
            Text(
                if (kind == AdminScheduleKind.BOOKABLE_CLASS) {
                    "Se reserva con cupo (clases). El portal y «Nueva reserva» usan estas franjas."
                } else {
                    "Sin reserva: la sesión se registra al pasar huella/rostro o de forma manual."
                },
                color = AdminColors.TextMuted,
                fontSize = 12.sp,
                modifier = Modifier.padding(top = 4.dp),
            )

            Spacer(Modifier.height(12.dp))
            Text("Servicio del catálogo", color = AdminColors.TextMuted, fontSize = 12.sp)
            Spacer(Modifier.height(4.dp))
            Box {
                OutlinedButton(
                    onClick = { serviceMenu = true },
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(10.dp),
                ) {
                    Text(
                        selectedService?.let { "${it.name} (${it.type.labelEs()})" }
                            ?: if (filteredServices.isEmpty()) "Sin servicios compatibles" else "Opcional — sin servicio",
                        color = AdminColors.Text,
                        modifier = Modifier.fillMaxWidth(),
                    )
                }
                DropdownMenu(expanded = serviceMenu, onDismissRequest = { serviceMenu = false }) {
                    DropdownMenuItem(
                        text = { Text("Sin servicio") },
                        onClick = {
                            serviceId = ""
                            serviceMenu = false
                        },
                    )
                    filteredServices.forEach { service ->
                        DropdownMenuItem(
                            text = { Text("${service.name} (${service.type.labelEs()})") },
                            onClick = {
                                serviceId = service.id
                                serviceMenu = false
                            },
                        )
                    }
                }
            }

            Spacer(Modifier.height(12.dp))
            Text(
                if (kind == AdminScheduleKind.OPEN_FLOOR) "Entrenador *" else "Entrenador",
                color = AdminColors.TextMuted,
                fontSize = 12.sp,
            )
            Spacer(Modifier.height(4.dp))
            Box {
                OutlinedTextField(
                    value = trainerQuery,
                    onValueChange = {
                        trainerQuery = it
                        trainerMenu = true
                        if (trainerId.isNotEmpty()) {
                            val match = trainers.firstOrNull { t -> t.id == trainerId }
                            if (match == null || match.displayName != it) trainerId = ""
                        }
                    },
                    modifier = Modifier.fillMaxWidth(),
                    singleLine = true,
                    placeholder = { Text("Buscar trabajadores...", color = AdminColors.TextMuted) },
                    leadingIcon = { Icon(Icons.Outlined.Search, null, tint = AdminColors.TextMuted) },
                    shape = RoundedCornerShape(10.dp),
                    colors = OutlinedTextFieldDefaults.colors(
                        focusedBorderColor = AdminColors.Primary,
                        unfocusedBorderColor = AdminColors.Outline,
                    ),
                )
                DropdownMenu(
                    expanded = trainerMenu && filteredTrainers.isNotEmpty(),
                    onDismissRequest = { trainerMenu = false },
                ) {
                    filteredTrainers.forEach { t ->
                        DropdownMenuItem(
                            text = { Text(t.displayName) },
                            onClick = {
                                trainerId = t.id
                                trainerQuery = t.displayName
                                trainerMenu = false
                            },
                        )
                    }
                }
            }

            Spacer(Modifier.height(12.dp))
            Text("Día", color = AdminColors.TextMuted, fontSize = 12.sp)
            Spacer(Modifier.height(4.dp))
            Box {
                OutlinedButton(
                    onClick = { dayMenu = true },
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(10.dp),
                ) {
                    Text(weekday.labelEs, color = AdminColors.Text, modifier = Modifier.fillMaxWidth())
                }
                DropdownMenu(expanded = dayMenu, onDismissRequest = { dayMenu = false }) {
                    AdminWeekday.entries.forEach { day ->
                        DropdownMenuItem(
                            text = { Text(day.labelEs) },
                            onClick = {
                                weekday = day
                                dayMenu = false
                            },
                        )
                    }
                }
            }

            Spacer(Modifier.height(12.dp))
            Text("Rango horario", color = AdminColors.TextMuted, fontSize = 12.sp)
            Spacer(Modifier.height(4.dp))
            OutlinedTextField(
                value = timeRange,
                onValueChange = { timeRange = it },
                modifier = Modifier.fillMaxWidth(),
                singleLine = true,
                placeholder = { Text("05:00–06:00", color = AdminColors.TextMuted) },
                shape = RoundedCornerShape(10.dp),
                colors = OutlinedTextFieldDefaults.colors(
                    focusedBorderColor = AdminColors.Primary,
                    unfocusedBorderColor = AdminColors.Outline,
                ),
            )

            Spacer(Modifier.height(12.dp))
            Text("Franja / servicio", color = AdminColors.TextMuted, fontSize = 12.sp)
            Spacer(Modifier.height(4.dp))
            OutlinedTextField(
                value = titleField,
                onValueChange = { titleField = it },
                modifier = Modifier.fillMaxWidth(),
                singleLine = true,
                placeholder = { Text("Crossfit (Level 1)", color = AdminColors.TextMuted) },
                shape = RoundedCornerShape(10.dp),
                colors = OutlinedTextFieldDefaults.colors(
                    focusedBorderColor = AdminColors.Primary,
                    unfocusedBorderColor = AdminColors.Outline,
                ),
            )

            Spacer(Modifier.height(12.dp))
            Text("Capacidad", color = AdminColors.TextMuted, fontSize = 12.sp)
            Spacer(Modifier.height(4.dp))
            OutlinedTextField(
                value = capacityText,
                onValueChange = { capacityText = it.filter { ch -> ch.isDigit() }.take(4) },
                modifier = Modifier.fillMaxWidth(),
                singleLine = true,
                shape = RoundedCornerShape(10.dp),
                colors = OutlinedTextFieldDefaults.colors(
                    focusedBorderColor = AdminColors.Primary,
                    unfocusedBorderColor = AdminColors.Outline,
                ),
            )

            formError?.let {
                Spacer(Modifier.height(8.dp))
                Text(it, color = AdminColors.Error, fontSize = 13.sp)
            }

            Spacer(Modifier.height(16.dp))
            Row(
                Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(10.dp, Alignment.End),
            ) {
                OutlinedButton(onClick = onDismiss, enabled = !saving, shape = RoundedCornerShape(10.dp)) {
                    Text("Cancelar", color = AdminColors.TextMuted)
                }
                Button(
                    onClick = {
                        formError = null
                        if (kind == AdminScheduleKind.OPEN_FLOOR && trainerId.isBlank()) {
                            formError = "Selecciona un entrenador para piso abierto."
                            return@Button
                        }
                        val range = parseTimeRange(timeRange)
                        if (range == null) {
                            formError = "Usa un rango válido, p. ej. 05:00–06:00."
                            return@Button
                        }
                        val (start, end) = range
                        if (titleField.trim().isEmpty()) {
                            formError = "Indica el nombre de la franja / servicio."
                            return@Button
                        }
                        val capacity = capacityText.toIntOrNull()
                        if (capacity == null || capacity < 1) {
                            formError = "La capacidad debe ser un número ≥ 1."
                            return@Button
                        }
                        onSave(
                            AdminScheduleUpsert(
                                weekday = weekday.iso,
                                startTime = start,
                                endTime = end,
                                title = titleField.trim(),
                                capacity = capacity,
                                kind = kind,
                                trainerId = trainerId.ifBlank { null },
                                serviceId = serviceId.ifBlank { null },
                                weekStart = (initial?.weekStart ?: defaultWeekStart)?.take(10),
                            ),
                        )
                    },
                    enabled = !saving,
                    shape = RoundedCornerShape(10.dp),
                    colors = ButtonDefaults.buttonColors(
                        containerColor = AdminColors.Primary,
                        contentColor = AdminColors.OnBrand,
                    ),
                ) {
                    Text(if (saving) "…" else "Guardar", fontWeight = FontWeight.SemiBold)
                }
            }
        }
    }
}

@Composable
private fun DuplicateWeekDialog(
    sourceWeek: WeekRange,
    saving: Boolean,
    onDismiss: () -> Unit,
    onDuplicate: (targetWeekStart: String, replaceExisting: Boolean) -> Unit,
) {
    var targetDate by remember { mutableStateOf(WeekRange.ofOffset(sourceWeek.weekOffset + 1).mondayIso) }
    var replaceExisting by remember { mutableStateOf(false) }
    var validationError by remember { mutableStateOf<String?>(null) }

    val targetMonday = remember(targetDate) {
        parseFlexibleDateToIso(targetDate)?.let { runCatching { mondayOfWeek(it) }.getOrNull() }
    }
    val targetRangeLabel = targetMonday?.let { WeekRange.displayRangeEs(it) }

    fun submit() {
        val iso = parseFlexibleDateToIso(targetDate) ?: run {
            validationError = "Indica una fecha válida (DD/MM/AAAA o AAAA-MM-DD)."
            return
        }
        val monday = runCatching { mondayOfWeek(iso) }.getOrNull()
        if (monday == null) {
            validationError = "Fecha no válida."
            return
        }
        if (monday == sourceWeek.mondayIso) {
            validationError = "La semana destino debe ser distinta a la semana origen."
            return
        }
        validationError = null
        onDuplicate(monday, replaceExisting)
    }

    Dialog(onDismissRequest = { if (!saving) onDismiss() }) {
        Column(
            Modifier
                .widthIn(max = 440.dp)
                .fillMaxWidth()
                .clip(RoundedCornerShape(16.dp))
                .background(AdminColors.SurfaceContainer)
                .padding(20.dp),
        ) {
            Row(
                Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Text("Duplicar semana", fontWeight = FontWeight.Bold, fontSize = 18.sp)
                IconButton(onClick = onDismiss, enabled = !saving) {
                    Icon(Icons.Outlined.Close, "Cerrar", tint = AdminColors.TextMuted)
                }
            }
            Spacer(Modifier.height(8.dp))
            Text(
                "Copia todos los horarios de la semana origen a otra semana. Si la origen no tiene horarios propios, se usan las plantillas globales.",
                color = AdminColors.TextMuted,
                fontSize = 13.sp,
            )
            Spacer(Modifier.height(12.dp))
            ScheduleDetailRow("Semana origen", sourceWeek.labelEs())
            Spacer(Modifier.height(8.dp))
            Text("Fecha de la semana destino", color = AdminColors.TextMuted, fontSize = 12.sp)
            Spacer(Modifier.height(4.dp))
            AdminDateField(
                value = targetDate,
                onValueChange = {
                    targetDate = it
                    validationError = null
                },
                enabled = !saving,
                placeholder = "AAAA-MM-DD o DD/MM/AAAA",
                outputFormat = AdminDateOutputFormat.ISO,
            )
            targetRangeLabel?.let {
                Text(
                    "Semana destino: $it",
                    color = AdminColors.TextMuted,
                    fontSize = 12.sp,
                    modifier = Modifier.padding(top = 4.dp),
                )
            }
            Spacer(Modifier.height(8.dp))
            Row(
                verticalAlignment = Alignment.CenterVertically,
                modifier = Modifier.fillMaxWidth(),
            ) {
                Checkbox(
                    checked = replaceExisting,
                    onCheckedChange = { replaceExisting = it },
                    enabled = !saving,
                    colors = CheckboxDefaults.colors(checkedColor = AdminColors.Primary),
                )
                Text(
                    "Reemplazar horarios existentes en la semana destino",
                    fontSize = 13.sp,
                    color = AdminColors.Text,
                    modifier = Modifier.weight(1f),
                )
            }
            validationError?.let {
                Text(it, color = AdminColors.Error, fontSize = 12.sp, modifier = Modifier.padding(top = 4.dp))
            }
            Spacer(Modifier.height(16.dp))
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
                TextButton(onClick = onDismiss, enabled = !saving) {
                    Text("Cancelar", color = AdminColors.TextMuted)
                }
                Spacer(Modifier.width(8.dp))
                Button(
                    onClick = { submit() },
                    enabled = !saving,
                    colors = ButtonDefaults.buttonColors(
                        containerColor = AdminColors.Primary,
                        contentColor = AdminColors.OnBrand,
                    ),
                ) {
                    Text(if (saving) "…" else "Duplicar", fontWeight = FontWeight.SemiBold)
                }
            }
        }
    }
}
